import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
} from '@mui/material';
import { AdminFeatureRequest } from '../../types/featureRequests';

interface ShipDialogProps {
  request: AdminFeatureRequest | null;
  onClose: () => void;
  onConfirm: (releaseNote: string, isPublic: boolean) => Promise<void>;
}

const ShipDialog: React.FC<ShipDialogProps> = ({ request, onClose, onConfirm }) => {
  const [releaseNote, setReleaseNote] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setReleaseNote('');
    setIsPublic(true);
    onClose();
  };

  const handleConfirm = async () => {
    if (!releaseNote.trim()) return;
    setSubmitting(true);
    try {
      await onConfirm(releaseNote.trim(), isPublic);
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!request} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Ship "{request?.title}"</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This marks the request shipped, publishes a changelog entry, and emails the author and
          every studio who voted for it.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Release note"
          placeholder="What shipped, in plain language for studio owners"
          value={releaseNote}
          onChange={(e) => setReleaseNote(e.target.value)}
          inputProps={{ maxLength: 2000 }}
        />
        <FormControlLabel
          sx={{ mt: 1.5 }}
          control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
          label="Show on the public changelog"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || !releaseNote.trim()}>
          {submitting ? <CircularProgress size={20} color="inherit" /> : 'Ship it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShipDialog;
